import { z } from 'zod';

// ==========================================
// Question Import Validation Schemas
// ==========================================

/**
 * Schema for validating individual question data from CSV/JSON imports
 */
export const questionImportSchema = z.object({
  id: z.string().optional(),
  question: z.string().optional(),
  question_text: z.string().optional(),
  question_type: z.enum(['multiple_choice', 'multiple_select', 'true_false']).optional().default('multiple_choice'),
  options: z.array(z.string()).optional(),
  choices: z.array(z.string()).optional(),
  correct_options: z.array(z.string()).optional(),
  correct_answer: z.union([z.string(), z.array(z.string())]).optional(),
  explanation: z.string().optional(),
  answer: z.string().optional(),
}).refine(
  (data) => {
    // Must have either question or question_text
    return data.question || data.question_text;
  },
  { message: 'Question text is required (either "question" or "question_text" field)' }
).refine(
  (data) => {
    // Must have some form of correct answer
    return data.correct_answer || data.correct_options || data.answer;
  },
  { message: 'Correct answer is required (either "correct_answer", "correct_options", or "answer" field)' }
);

/**
 * Schema for validating an array of questions
 */
export const questionImportArraySchema = z.array(questionImportSchema).min(1, {
  message: 'At least one valid question is required',
});

/**
 * Schema for CSV row validation
 */
export const csvQuestionRowSchema = z.object({
  question: z.string().optional(),
  question_text: z.string().optional(),
  question_type: z.string().optional(),
  option1: z.string().optional(),
  option2: z.string().optional(),
  option3: z.string().optional(),
  option4: z.string().optional(),
  option5: z.string().optional(),
  option6: z.string().optional(),
  option7: z.string().optional(),
  option8: z.string().optional(),
  option9: z.string().optional(),
  option10: z.string().optional(),
  choice1: z.string().optional(),
  choice2: z.string().optional(),
  choice3: z.string().optional(),
  choice4: z.string().optional(),
  choice5: z.string().optional(),
  correct_answer: z.string().optional(),
  correct_options: z.string().optional(),
  explanation: z.string().optional(),
}).refine(
  (data) => data.question || data.question_text,
  { message: 'Question text is required' }
);

// ==========================================
// Contact Form Validation Schema
// ==========================================

export const contactFormSchema = z.object({
  first_name: z.string()
    .min(1, 'First name is required')
    .max(100, 'First name is too long')
    .regex(/^[\p{L}\s'-]+$/u, 'First name contains invalid characters'),
  last_name: z.string()
    .min(1, 'Last name is required')
    .max(100, 'Last name is too long')
    .regex(/^[\p{L}\s'-]+$/u, 'Last name contains invalid characters'),
  email: z.string()
    .email('Invalid email address')
    .max(255, 'Email is too long'),
  user_type: z.enum(['student', 'resident', 'professional', 'educator', 'institution', 'other'], {
    errorMap: () => ({ message: 'Please select a valid user type' }),
  }),
  subject: z.string()
    .min(1, 'Subject is required')
    .max(200, 'Subject is too long'),
  message: z.string()
    .min(10, 'Message must be at least 10 characters')
    .max(5000, 'Message is too long (max 5000 characters)'),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// ==========================================
// Search Query Validation
// ==========================================

/**
 * Sanitizes search input to prevent injection attacks
 */
export const sanitizeSearchQuery = (query: string): string => {
  // Remove or escape potentially dangerous characters
  return query
    .trim()
    .slice(0, 500) // Limit length
    .replace(/[%_\\]/g, '\\$&'); // Escape SQL LIKE wildcards
};

/**
 * Schema for search queries
 */
export const searchQuerySchema = z.string()
  .max(500, 'Search query is too long')
  .transform(sanitizeSearchQuery);

// ==========================================
// File Upload Validation
// ==========================================

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = ['application/json', 'text/csv', 'text/plain'];
const ALLOWED_EXTENSIONS = ['.json', '.csv'];

export const validateUploadFile = (file: File): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `File is too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB` };
  }

  // Check file type
  const isValidType = ALLOWED_FILE_TYPES.includes(file.type) ||
    file.type === '' && ALLOWED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

  if (!isValidType) {
    return { valid: false, error: 'Invalid file type. Only JSON and CSV files are allowed.' };
  }

  // Check extension
  const hasValidExtension = ALLOWED_EXTENSIONS.some(ext =>
    file.name.toLowerCase().endsWith(ext)
  );

  if (!hasValidExtension) {
    return { valid: false, error: 'Invalid file extension. Only .json and .csv files are allowed.' };
  }

  return { valid: true };
};

// ==========================================
// CSRF Token Generation
// ==========================================

/**
 * Generates a CSRF token using Web Crypto API
 */
export const generateCSRFToken = async (): Promise<string> => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Stores CSRF token in sessionStorage
 */
export const storeCSRFToken = (token: string): void => {
  sessionStorage.setItem('csrf_token', token);
};

/**
 * Retrieves CSRF token from sessionStorage
 */
export const getStoredCSRFToken = (): string | null => {
  return sessionStorage.getItem('csrf_token');
};

/**
 * Validates a CSRF token against the stored token
 */
export const validateCSRFToken = (token: string): boolean => {
  const storedToken = getStoredCSRFToken();
  if (!storedToken || !token) return false;

  // Constant-time comparison to prevent timing attacks
  if (storedToken.length !== token.length) return false;

  let result = 0;
  for (let i = 0; i < storedToken.length; i++) {
    result |= storedToken.charCodeAt(i) ^ token.charCodeAt(i);
  }

  return result === 0;
};

// ==========================================
// Rate Limiting
// ==========================================

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

const rateLimitStore = new Map<string, RateLimitEntry>();

/**
 * Client-side rate limiting
 * @param key - Unique identifier for the action being rate-limited
 * @param maxRequests - Maximum number of requests allowed
 * @param windowMs - Time window in milliseconds
 * @returns Whether the action is allowed
 */
export const checkRateLimit = (key: string, maxRequests: number = 5, windowMs: number = 60000): boolean => {
  const now = Date.now();
  const entry = rateLimitStore.get(key);

  // Clean up expired entries periodically
  if (rateLimitStore.size > 100) {
    for (const [k, v] of rateLimitStore.entries()) {
      if (v.resetTime < now) {
        rateLimitStore.delete(k);
      }
    }
  }

  if (!entry || entry.resetTime < now) {
    // First request or window expired
    rateLimitStore.set(key, {
      count: 1,
      resetTime: now + windowMs,
    });
    return true;
  }

  if (entry.count >= maxRequests) {
    return false;
  }

  entry.count++;
  return true;
};

/**
 * Gets remaining requests for a rate limit key
 */
export const getRateLimitRemaining = (key: string, maxRequests: number = 5): number => {
  const entry = rateLimitStore.get(key);
  if (!entry || entry.resetTime < Date.now()) {
    return maxRequests;
  }
  return Math.max(0, maxRequests - entry.count);
};

/**
 * Gets time until rate limit resets in milliseconds
 */
export const getRateLimitResetTime = (key: string): number => {
  const entry = rateLimitStore.get(key);
  if (!entry) return 0;
  return Math.max(0, entry.resetTime - Date.now());
};

// ==========================================
// Input Sanitization Utilities
// ==========================================

/**
 * Sanitizes HTML to prevent XSS
 */
export const sanitizeHtml = (input: string): string => {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
};

/**
 * Validates and sanitizes URL
 */
export const sanitizeUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
};
